/**
 * 콜봇 월 정산 목 — 개발 조작의 값 목록(마감 상태·건수 대사·수납·정산 시작일 PUT·내보내기 오류 장면).
 *
 * settlementMock.js(화면이 늘 불러오는 API 선택기)와 settlementMockEngine.js 가 같이 쓴다. 선택기가 엔진·원천
 * (settlementMockData.js — 9월 예시 사용량)을 정적으로 끌어오지 않게 값만 따로 둔다. 운영 빌드에는 목 쪽에서
 * 이 파일만 남는다(엔진·원천은 settlementMock.js 의 비운영 require 로만 실린다).
 *
 * 순수 모듈이다(import 없음). node --test 가 그대로 읽는다(엔진도 같은 값을 다시 내보낸다).
 */

/** 개발 조작의 마감 상태 값(settlementMockControls.STATUS_OPTIONS 와 같은 순서) */
export const STATUS_VALUES = Object.freeze([
  'estimating',
  'closing',
  'review',
  'review_reopened',
  'confirmed_system',
  'confirmed_human',
  'failed',
]);

/**
 * 개발 조작의 건수 대사 시나리오 — [vox 건수 확인]과 자동 마감의 vox 조회가 같이 쓴다.
 * too_many_calls: 불일치인데 통화가 많아 누락 통화 ID 를 끝까지 못 읽음(`missing_unavailable_reason`).
 * rate_limited: 대사는 429, 자동 마감은 vox 일시 실패로 마감 실패(VOX_REFRESH_FAILED).
 * not_provisioned: 대사는 409, 자동 마감은 vox 없이 계속 — 웹 사용량 미확인(WEB_USAGE_UNVERIFIED)으로 검토 필요.
 * web_voice_misread: 건수는 일치, 웹 요약이 웹 텍스트 절반을 웹 음성으로 나눔(원가로 가르기 전의 예전 규칙) — 끝난 달은
 *   웹 음성 원가 불일치(WEB_VOICE_COST_MISMATCH).
 */
export const RECON_VALUES = Object.freeze([
  'match',
  'mismatch',
  'too_many_calls',
  'rate_limited',
  'not_provisioned',
  'web_voice_misread',
]);

/**
 * 개발 조작의 수납 장면(병원·달마다, settlementMockControls.PAYMENT_OPTIONS 와 같은 순서) — settlementMockPayment.js 가 이 값으로
 * 정산 상세의 수납 칸 넷(`payment_scope`·`payment`·`payment_error`·`payment_confirm_preview`, 계약 묶음 9.1), 확정 고정의 409(9.2),
 * 재수정 정지 표식의 503(9.3)을 만든다. 기본은 off 다. 결제 요청 문서가 있는 장면(awaiting_deposit ~ held_invalid,
 * ledger_unreadable)은 확정 달이면 그 장면의 수납 상태를, 검토 필요(재수정) 달이면 멈춤(suspended · REOPENED)을 보인다.
 * 다만 확정 달에 받을 돈이 없으면(목표 ≤ 받은 순액 — 목표 0 인 내부 회사, 기본료 제외에서 기본료 줄만 있는 달, 낮춰 다시 확정한
 * paid) 장면이 입금 대기·기한 지남·입금 받음·결제 중·처리 대기 승인이어도 장부는 결제 완료나 계좌 환불 필요다(결제 계획 7.3 — 그
 * 상태들은 차액 > 0 일 때만). 내부 회사의 준비 버전 사유는 INTERNAL 이다(계약 묶음 3.4).
 * off: 결제 채널이 꺼진 환경(지금 모든 환경) — payment_scope 'inactive', 나머지 셋 null. 확정에 expected_rev 가 없어도 된다.
 * before_start: 수납 시작 월 이전 달 — 'before_start', 나머지 셋 null. 확정에 expected_rev 가 없어도 된다.
 * in_no_doc: 결제가 걸린 달인데 결제 요청 문서가 아직 없음(확정 직후 계획기가 돌기 전) — 'in', payment null(한 줄 숨김).
 *   검토 필요 달은 미리보기(받은 돈 0)를 준다.
 * awaiting_deposit: 계좌이체 · 기본료 제외 · 입금 대기 — 기한은 정산 달의 다음 달 말일. 엔진 시계가 그 기한을 지나면 기한 지남.
 * overdue: awaiting_deposit 과 같되 이미 지난 기한(그 달의 보통 기한이 지났으면 그것, 아니면 어제 — 기한 규칙 D-DUE-DATE 가 열려 있어
 *   목의 흉내)이라 기한 지남(`pw.overdue`)이 참이다.
 * paid: 계좌이체 · 기본료 제외 — 장면을 고른 때의 목표 합계를 계좌로 받은 것으로 둔다(순액 고정). 금액을 고쳐 재확정하면 남은 금액의
 *   부호로 입금 대기·결제 완료·계좌 환불 필요가 갈린다.
 * pw_pending: 결제 요청 문서는 있는데 PW 장부가 아직 없음 — `pw`·`applied_seq` null(한 줄의 '반영 중').
 * processing: 카드 · 정산서 전부 · 결제 중 — 재수정 창의 결제 중 배너.
 * scheduled: 카드 · 정산서 전부 · 승인됨(처리 대기) — 재수정 창의 처리 대기 승인 배너.
 * held_invalid: 보류 INVALID_STATEMENT(정산서 무결성 검사 실패) — 목표·남은 금액·기한 null, 장부는 준비 전. 미리보기도 null.
 * state_unreadable: 결제 쪽 판단(채널·요청 문서) 읽기 실패 — payment_scope null + PAYMENT_STATE_UNREADABLE. 확정은 결제가 걸린 달로
 *   보고(expected_rev 필수), 재수정은 늘 503 PAYMENT_STOP_FAILED(요청 문서를 읽지 못해도 막는 쪽).
 * ledger_unreadable: PW 장부 거울만 읽기 실패 — 'in' + PW_LEDGER_UNREADABLE, payment·미리보기 null.
 */
export const PAYMENT_VALUES = Object.freeze([
  'off',
  'before_start',
  'in_no_doc',
  'awaiting_deposit',
  'overdue',
  'paid',
  'pw_pending',
  'processing',
  'scheduled',
  'held_invalid',
  'state_unreadable',
  'ledger_unreadable',
]);

/**
 * 개발 조작 — 다음 PUT settlement-start 하나의 오류 장면(settlementMockControls.START_FAULT_OPTIONS 와 같은 순서, 한 번 쓰이면 none).
 * 계약서(2026-11-02 월 정산 2·3단계) 2절. call-api 요청서 3-3 의 처리 순서를 흉내 낸다.
 * rev_changed: 그 사이 다른 사람이 먼저 저장 — 엔진이 실제로 rev 를 올리고 409 SETTLEMENT_START_REV_CHANGED.
 * audit_unavailable: CAS 뒤 recordNow 실패 — 보상 CAS 로 시작일을 되돌리고(rev + 1, AUDIT_ROLLBACK) 503 AUDIT_UNAVAILABLE.
 * discard_conflict: 저장은 되고 지울 정산서(acknowledge_discard 달 전부)가 그사이 바뀐 것처럼 남는다 — discard_conflict(남은 정산서).
 * confirmed_during_change: 저장은 되고 before_start 로 간 달 하나에 그사이 확정이 쓰였다 — confirmed_during_change.
 */
export const START_FAULT_VALUES = Object.freeze([
  'none',
  'rev_changed',
  'audit_unavailable',
  'discard_conflict',
  'confirmed_during_change',
]);

/**
 * 개발 조작 — 다음 정산 내보내기 하나의 오류 장면(settlementMockControls.EXPORT_FAULT_OPTIONS 와 같은 순서, 한 번 쓰이면 none).
 * call-api 요청서 9절 "에러 정리". support_session_ended 의 reason 은 봉투 error 안(EXPIRED), unknown_company 는 고른 id 첫 개를
 * details.unknown_company_ids 로, timeout 은 응답 없는 axios 오류(code ECONNABORTED), not_found 는 라우트 없음 404.
 */
export const EXPORT_FAULT_VALUES = Object.freeze([
  'none',
  'audit_unavailable',
  'perm_unavailable',
  'support_session_ended',
  'permission_denied',
  'unknown_company',
  'export_failed',
  'timeout',
  'not_found',
]);
